import { useCallback, useEffect, useId, useRef, useState } from 'react';

import { Dialog } from 'radix-ui';

import Close from '@quick/assets/icons/close.svg';
import { Button } from '@quick/components/Button';
import { cn } from '@quick/utils';

import type { ModalProps } from './data';
import ModalContext from './ModalContext';

/**
 * @param trigger pass a Dialog.Trigger component
 * @description Use w-max or w-screen on your content. Overflow behavior must be handled by children.
 */
function Modal({
  children,
  titles = {},
  isOpen = true,
  events = {},
  buttonsOverContent = false,
  className = 'z-10',
  contentClassName,
  hideClose,
  trigger,
  onClose,
}: ModalProps) {
  const [status, setStatus] = useState(isOpen);

  const didExecuteOnClose = useRef(!isOpen);
  const onCloseAction = onClose ?? events?.onClose;

  useEffect(() => {
    setStatus(isOpen);
  }, [isOpen, setStatus]);

  useEffect(() => {
    if (status) {
      // clear when open
      didExecuteOnClose.current = false;
      return;
    }
    if (didExecuteOnClose.current) return;
    didExecuteOnClose.current = true;
    onCloseAction?.();
  }, [status, onCloseAction]);

  const hasTrigger = !!trigger;

  const onCloseAutoFocus = useCallback(() => {
    if (hasTrigger) return;
    // get list of all open modals and focus the last modal close button or itself
    const modalList: NodeListOf<HTMLDivElement> = document.querySelectorAll(
      `[id^="Modal_Content_"]`
    );

    if (modalList.length > 0) {
      const highestModal = Array.from(modalList).at(-1);
      const hideButton = highestModal?.firstElementChild as HTMLElement;
      if (hideButton instanceof HTMLButtonElement) {
        hideButton.focus();
      } else {
        highestModal?.focus();
      }
    }
  }, [hasTrigger]);
  const overlayRef = useRef<HTMLDivElement>(null);

  const onInteractOutside = useCallback(
    (
      e: Parameters<
        NonNullable<Dialog.DialogContentProps['onInteractOutside']>
      >[0]
    ) => {
      if (e.target !== overlayRef.current) {
        // avoid closing while interacting with timepicker calendar
        e.preventDefault();
      }
    },
    [overlayRef]
  );

  const { titleSuccess, titleReject } = titles;
  const { onSuccess, onReject } = events;
  const id = useId();

  return (
    <Dialog.Root open={status} onOpenChange={setStatus}>
      {trigger}
      <Dialog.Portal>
        <Dialog.Overlay
          className={cn(
            `bg-background-modal/25 fixed left-0 top-0 flex h-full w-full items-center
            justify-center px-5 py-5 md:px-10 lg:px-14 dark:bg-gray-400/25`,
            `data-[state=closed]:animate-fadeOut data-[state=open]:motion-safe:animate-fadeIn
            data-[state=closed]:duration-200 data-[state=open]:duration-500`,
            className
          )}
          ref={overlayRef}
        >
          <Dialog.Content
            className={cn(
              `bg-background-light relative z-20 flex max-h-full max-w-full flex-col gap-4
              rounded px-4 py-4 dark:border dark:border-gray-500 dark:bg-gray-700`,
              '[&>*]:max-h-full [&>*]:min-h-0 [&>*]:max-w-full',
              `data-[state=closed]:motion-safe:animate-slideDownAndFadeOut
              data-[state=open]:motion-safe:animate-slideUpAndFade
              data-[state=closed]:duration-200 data-[state=open]:duration-500`,
              contentClassName
            )}
            id={`Modal_Content_${id}`}
            onCloseAutoFocus={onCloseAutoFocus}
            onInteractOutside={onInteractOutside}
          >
            {!hideClose && (
              <Dialog.Close asChild>
                <button className="bg-brand hover:bg-brand-focus group absolute -right-4 -top-4 rounded p-2">
                  <Close width="14" height="16" className="text-black" />
                </button>
              </Dialog.Close>
            )}
            <ModalContext value={setStatus}>{children}</ModalContext>
            {(titleReject || titleSuccess) && (
              <div
                className={cn(
                  'z-10 flex shrink-0 flex-wrap items-center justify-center gap-4',
                  buttonsOverContent &&
                    'absolute bottom-4 left-1/2 right-1/2 mb-2'
                )}
              >
                {titleSuccess && onSuccess && (
                  <Button onClick={onSuccess} variant="approve">
                    {titleSuccess}
                  </Button>
                )}
                {titleReject && onReject && (
                  <Button onClick={onReject} variant="reject">
                    {titleReject}
                  </Button>
                )}
              </div>
            )}
          </Dialog.Content>
        </Dialog.Overlay>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

Modal.displayName = 'Modal';

export { Modal };
